import type { Edge } from '@xyflow/react';
import type { CSSProperties, ReactNode } from 'react';

import type { IWireConflict, IWireWarning } from './node-configs/auto-wire';

export interface IEdgeWiringData {
  autoWired?: boolean;
  wiringConflicts?: IWireConflict[];
  wiringWarnings?: IWireWarning[];
}

export type IWiringTone = 'success' | 'warning' | 'danger';

export interface IIncomingWiringBadge {
  sourceKey: string;
  edgeId: string;
  tone: IWiringTone;
  /** Short status for screen readers / inspector. */
  status: string;
  title: string;
  /** Full wiring statement — shown inline for warnings and conflicts. */
  detail?: string;
}

const badgeToneStyles: Record<IWiringTone, CSSProperties> = {
  // `--status-success`, not `--accent-teal`: this chip reports that auto-wiring
  // succeeded, which is a status, and `--accent-*` is the closed set of
  // user-assignable board-column colors (DEV-17060). It read teal only because
  // `--accent-teal` was undefined and the hardcoded fallback won; the token is
  // real now, so point at the one that means what this chip means.
  success: {
    color: 'var(--status-success)',
    background: 'color-mix(in srgb, var(--status-success) 14%, var(--surface))',
    borderColor: 'color-mix(in srgb, var(--status-success) 35%, transparent)',
  },
  warning: {
    color: 'var(--status-warning, #d97706)',
    background: 'color-mix(in srgb, var(--status-warning, #d97706) 14%, var(--surface))',
    borderColor: 'color-mix(in srgb, var(--status-warning, #d97706) 35%, transparent)',
  },
  danger: {
    color: 'var(--status-danger)',
    background: 'color-mix(in srgb, var(--status-danger) 14%, var(--surface))',
    borderColor: 'color-mix(in srgb, var(--status-danger) 35%, transparent)',
  },
};

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  width: 11,
  height: 11,
  'aria-hidden': true as const,
};

function WiringStatusIcon({ tone }: { tone: IWiringTone }): ReactNode {
  if (tone === 'success') {
    return (
      <svg {...iconProps}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (tone === 'warning') {
    return (
      <svg {...iconProps}>
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      </svg>
    );
  }
  return (
    <svg {...iconProps}>
      <circle cx={12} cy={12} r={10} />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

export function resolveEdgeWiringChip(data: IEdgeWiringData | undefined): {
  label: string;
  tone: IWiringTone;
  title: string;
} | null {
  if (!data) return null;
  const conflicts = data.wiringConflicts ?? [];
  const warnings = data.wiringWarnings ?? [];

  if (conflicts.length > 0) {
    return {
      label: 'Conflict',
      tone: 'danger',
      title: conflicts
        .map(
          (c) =>
            `${c.inputKey} is already wired (${c.existing}); this connection would use ${c.proposed}.`,
        )
        .join('\n'),
    };
  }
  if (warnings.length > 0) {
    const first = warnings[0];
    const label =
      first?.kind === 'unmapped-input' && first.inputKey === 'query'
        ? 'No query field'
        : 'No match';
    return {
      label,
      tone: 'warning',
      title: warnings.map((w) => w.message).join('\n'),
    };
  }
  if (data.autoWired) {
    return {
      label: 'Wired',
      tone: 'success',
      title: 'Input mapping references this upstream node.',
    };
  }
  return null;
}

export function buildIncomingWiringBadges(
  targetNodeId: string,
  edges: Edge[],
): IIncomingWiringBadge[] {
  const badges: IIncomingWiringBadge[] = [];
  for (const edge of edges) {
    if (edge.target !== targetNodeId) continue;
    const chip = resolveEdgeWiringChip(edge.data as IEdgeWiringData);
    if (!chip) continue;
    badges.push({
      sourceKey: edge.source,
      edgeId: edge.id,
      tone: chip.tone,
      status: chip.label,
      title: `${edge.source}: ${chip.label} — ${chip.title}`,
      detail: chip.tone === 'success' ? undefined : chip.title,
    });
  }
  return badges;
}

const iconBadgeBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '1px solid',
  flexShrink: 0,
};

export function IncomingWiringBadges({
  badges,
  layout = 'stack',
}: {
  badges: IIncomingWiringBadge[];
  layout?: 'stack' | 'wrap';
}) {
  if (badges.length === 0) return null;

  return (
    <div
      className={
        layout === 'wrap' ? 'node-incoming-wiring-list is-wrap' : 'node-incoming-wiring-list'
      }
    >
      {badges.map((b) => (
        <span
          key={b.edgeId}
          className="node-wire-icon"
          style={{ ...iconBadgeBase, ...badgeToneStyles[b.tone] }}
          title={b.title}
          aria-label={b.title}
          role="img"
        >
          <WiringStatusIcon tone={b.tone} />
        </span>
      ))}
    </div>
  );
}
